import React, { useState } from 'react';
import { Button } from 'antd';
import { DeleteOutlined, RightOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { AddedMetric, DraftMetric } from 'src/reducers/customMetrices';
import { DraftMetricCard } from './DraftMetricCard';
import { AvailableField } from './EnhancedSqlEditor';
import { CalculationFunction } from '../calculationMetadataTypes';

const useStyles = createUseStyles({
    card: {
        background: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        padding: '12px',
        marginBottom: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
            borderColor: '#1D6EC3',
            boxShadow: '0 2px 8px rgba(29, 110, 195, 0.1)',
        },
    },
    cardExpanded: {
        borderColor: '#1D6EC3',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        fontWeight: 500,
    },
    expandIcon: {
        fontSize: '12px',
        color: '#8c8c8c',
    },
    expandedContent: {
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #f0f0f0',
    },
    infoRow: {
        marginBottom: '8px',
        fontSize: '12px',
    },
    label: {
        color: '#8c8c8c',
        fontWeight: 500,
        marginRight: '8px',
    },
    sqlBox: {
        backgroundColor: '#f5f5f5',
        padding: '8px',
        borderRadius: '4px',
        fontFamily: 'monospace',
        fontSize: '11px',
        marginTop: '4px',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
    },
    buttonGroup: {
        display: 'flex',
        gap: '8px',
        marginTop: '12px',
    },
    deleteButton: {
        '&:hover': {
            color: '#ff4d4f !important',
        },
    },
    typeBadge: {
        fontSize: '11px',
        padding: '2px 8px',
        borderRadius: '3px',
        backgroundColor: '#f0f0f0',
        color: '#595959',
        marginLeft: '8px',
    },
    editingContent: {
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #f0f0f0',
    },
});

interface CollapsedMetricCardProps {
    metric: AddedMetric;
    onExpand: (id: string) => void;
    onDelete: (id: string) => void;
    onEdit: (id: string) => void;
    onSaveEdit: (id: string, updates: Partial<AddedMetric>) => void;
    onCancelEdit: (id: string) => void;
    availableFields: AvailableField[];
    calculationFunctions: CalculationFunction[];
    existingMetricNames: string[];
    selectedReportType: string;
}

export const CollapsedMetricCard: React.FC<CollapsedMetricCardProps> = ({
    metric,
    onExpand,
    onDelete,
    onEdit,
    onSaveEdit,
    onCancelEdit,
    availableFields,
    calculationFunctions,
    existingMetricNames,
    selectedReportType,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [editingDraft, setEditingDraft] = useState<DraftMetric>({
        id: metric.id,
        name: metric.name,
        prettyName: metric.prettyName,
        type: metric.type,
        sql: metric.sql,
    });

    const handleHeaderClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.delete-button')) {
            return;
        }
        onExpand(metric.id);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(metric.id);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingDraft({
            id: metric.id,
            name: metric.name,
            prettyName: metric.prettyName,
            type: metric.type,
            sql: metric.sql,
        });
        onEdit(metric.id);
    };

    const handleSaveEdit = (addedMetric: AddedMetric) => {
        onSaveEdit(metric.id, {
            prettyName: addedMetric.prettyName,
            type: addedMetric.type,
            sql: addedMetric.sql,
            name: addedMetric.name,
        });
    };

    const handleCancelEdit = () => {
        onCancelEdit(metric.id);
    };

    return (
        <div className={`${classes.card} ${metric.isExpanded ? classes.cardExpanded : ''}`}>
            <div className={classes.header} onClick={handleHeaderClick}>
                <div className={classes.title}>
                    {metric.isExpanded ? (
                        <DownOutlined className={classes.expandIcon} />
                    ) : (
                        <RightOutlined className={classes.expandIcon} />
                    )}
                    <span>{metric.prettyName}</span>
                    <span className={classes.typeBadge}>{metric.type}</span>
                </div>
                <Button
                    className={`delete-button ${classes.deleteButton}`}
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                />
            </div>

            {metric.isExpanded && !metric.isEditing && (
                <div className={classes.expandedContent}>
                    <div className={classes.infoRow}>
                        <span className={classes.label}>{t('Metric Name')}:</span>
                        <span style={{ fontFamily: 'monospace' }}>{metric.name}</span>
                    </div>

                    <div className={classes.infoRow}>
                        <span className={classes.label}>{t('SQL Expression')}:</span>
                        <div className={classes.sqlBox}>{metric.sql}</div>
                    </div>

                    <div className={classes.buttonGroup}>
                        <Button size="small" icon={<EditOutlined />} onClick={handleEdit}>
                            {t('Edit')}
                        </Button>
                    </div>
                </div>
            )}

            {metric.isExpanded && metric.isEditing && (
                <div className={classes.editingContent}>
                    <DraftMetricCard
                        data={editingDraft}
                        onChange={setEditingDraft}
                        onAdd={handleSaveEdit}
                        onCancel={handleCancelEdit}
                        availableFields={availableFields}
                        calculationFunctions={calculationFunctions}
                        existingMetricNames={existingMetricNames}
                        customColumnType="global"
                        selectedReportType={selectedReportType}
                        buttonLabel={t('Save Changes')}
                    />
                </div>
            )}
        </div>
    );
};

export default CollapsedMetricCard;

